import {
    CreateUploadRequestAssetType,
    CreateUploadRequestV2Mutation,
    FollowupQuestion,
    useCreatePostMutation,
    useCreateUploadRequestV2Mutation
} from "@/graphql/API";
import {provideApolloClient} from "@vue/apollo-composable";
import {apolloClient} from "@/providers/graphql.provider";
import {FetchResult} from "@apollo/client";
import {UploadRequest, UploadRequestKind} from "@/models/upload-request";
import axios from "axios";
import {OnSurveyAgentViewFinished} from "@/views/survey/components/SurveyAgentView.vue";
import {ConversationalSurveyRound} from "@/utils/conversationalSurveyService";

interface FileUploadConfig {
    protocol: string;
    method: string;
    headers: { [name: string]: string };
    url: string;
    fileName: string;
    provider: string;
    data: FormData | ArrayBuffer | Blob;
}

export interface UploadInput {
    isTextSurvey: boolean;
    campaignID: string;
    email?: string;
    name?: string;
    rating?: number;
    surveyFinishedPayload: OnSurveyAgentViewFinished;
    externalID?: string;
}

export class SurveyUploader {
    async upload(input: UploadInput) {
        provideApolloClient(apolloClient);

        let answer: string | undefined;
        let audio: { name: string; provider: string } | undefined;
        let followupQuestions: FollowupQuestion[] | undefined;

        if (input.isTextSurvey) {
            answer = input.surveyFinishedPayload.conversationalSurveyRounds?.[0].answer;
            if (!answer) {
                throw new Error("Unexpectedly answer is undefined")
            }

            followupQuestions = input.surveyFinishedPayload.conversationalSurveyRounds
                ?.slice(1)
                .filter((round: ConversationalSurveyRound) => round.question && round.answer)
                .map((question: ConversationalSurveyRound): FollowupQuestion => ({
                    question: question.question,
                    timestamp: 0,
                    answer: question.answer,
                }));
        } else {
            if (!input.surveyFinishedPayload.recordedAudio) {
                throw new Error("Unexpectedly audio is undefined")
            }

            const audioUploadResponse = await this.uploadAudio(input.surveyFinishedPayload.recordedAudio);
            audio = {
                name: audioUploadResponse.fileName,
                provider: audioUploadResponse.provider,
            }

            followupQuestions = input.surveyFinishedPayload.conversationalSurveyRounds
                ?.slice(1)
                .filter((round: ConversationalSurveyRound) => round.question && round.questionTimestamp)
                .map((question: ConversationalSurveyRound): FollowupQuestion => ({
                    question: question.question || "",
                    timestamp: question.questionTimestamp || 0,
                }));
        }

        const { mutate } = useCreatePostMutation({
            variables: {
                audio: audio,
                answer: answer,
                campaignID: input.campaignID,
                email: input.email,
                name: input.name,
                rating: input.rating,
                externalID: input.externalID,
                followupQuestions,
            },
        });

        await mutate();
    }

    // Private -

    private async uploadAudio(blob: Blob): Promise<FileUploadConfig> {
        const { mutate } = useCreateUploadRequestV2Mutation({
            variables: {
                assetType: CreateUploadRequestAssetType.Audio
            },
        });

        const mutationResponse: FetchResult<CreateUploadRequestV2Mutation> | null = await mutate();
        if (!mutationResponse) {
            throw new Error("Unexpected empty response from CreateUploadRequestV2Mutation")
        }

        const uploadRequest = mutationResponse.data?.createUploadRequest as UploadRequest;
        const uploadConfig = this.prepareFileUploadConfig(uploadRequest, blob);
        await axios(uploadConfig);
        return uploadConfig;
    }

    private prepareFileUploadConfig(uploadRequestResponse: UploadRequest, data: ArrayBuffer | Blob): FileUploadConfig {
        let dataForUpload: FormData | ArrayBuffer | Blob = data;
        const headers = uploadRequestResponse.headers.reduce((headers: { [name: string]: string }, header) => {
            headers[header.key] = header.value;
            return headers;
        }, {});

        if (uploadRequestResponse.kind === UploadRequestKind.MultipartForm) {
            const form = new FormData();
            form.append(uploadRequestResponse.fileParameterName, new Blob([data]), uploadRequestResponse.fileName);

            uploadRequestResponse.parameters.forEach((parameter) => {
                form.append(parameter.key, parameter.value);
            });

            headers['Content-Type'] = 'multipart/form-data';
            dataForUpload = form;
        }

        return {
            protocol: 'https',
            method: uploadRequestResponse.httpMethod,
            headers: headers,
            url: uploadRequestResponse.url,
            fileName: uploadRequestResponse.fileName,
            provider: uploadRequestResponse.provider,
            data: dataForUpload,
        };
    }
}
